/**
 * GlobalSearch Component - CSS-First Design Token Architecture
 *
 * Global search modal with keyboard shortcut (Cmd+K / Ctrl+K).
 * Fast native search (<500ms) across all content types.
 *
 * WCAG 2.1 AA compliant with full keyboard navigation.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X } from 'lucide-react'

interface SearchResult {
  type: 'page' | 'project' | 'organization' | 'role'
  title: string
  description?: string
  path: string
  keywords?: string[]
}

// Searchable content index
const SEARCH_INDEX: SearchResult[] = [
  // Pages
  { type: 'page', title: 'Home', path: '/', description: 'Landing page', keywords: ['home', 'landing', 'intro'] },
  { type: 'page', title: 'Background', path: '/background', description: 'Professional background', keywords: ['experience', 'history', 'career'] },
  { type: 'page', title: 'Interests', path: '/interests', description: 'Personal interests', keywords: ['hobbies', 'passion', 'outside work'] },
  { type: 'page', title: 'Writings', path: '/writings', description: 'Articles and blog posts', keywords: ['blog', 'articles', 'posts', 'writing'] },
  { type: 'page', title: 'Projects', path: '/projects', description: 'Portfolio projects', keywords: ['work', 'portfolio', 'code', 'github'] },
  { type: 'page', title: 'CV', path: '/cv', description: 'Curriculum Vitae', keywords: ['resume', 'cv', 'curriculum', 'vitae'] },
  { type: 'page', title: 'Portfolio Intelligence', path: '/portfolio-intelligence', description: 'AI-powered portfolio insights', keywords: ['ai', 'intelligence', 'insights', 'analytics'] },
  { type: 'page', title: 'Organizations', path: '/organizations', description: 'Companies and evaluations', keywords: ['companies', 'employers', 'transparency'] },
  { type: 'page', title: 'Roles', path: '/roles', description: 'Job positions', keywords: ['jobs', 'positions', 'openings'] },
  { type: 'page', title: 'Admin Dashboard', path: '/admin', description: 'Admin panel', keywords: ['admin', 'dashboard', 'management'] },
]

interface GlobalSearchProps {
  isOpen: boolean
  onClose: () => void
}

export function GlobalSearch({ isOpen, onClose }: GlobalSearchProps) {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Search function (case-insensitive, searches title, description, keywords)
  const searchResults = useMemo(() => {
    if (!query.trim()) return []

    const searchTerm = query.toLowerCase().trim()
    const startTime = performance.now()

    const results = SEARCH_INDEX.filter((item) => {
      const titleMatch = item.title.toLowerCase().includes(searchTerm)
      const descMatch = item.description?.toLowerCase().includes(searchTerm)
      const keywordMatch = item.keywords?.some((kw) => kw.includes(searchTerm))

      return titleMatch || descMatch || keywordMatch
    }).slice(0, 8) // Limit to 8 results for performance

    const endTime = performance.now()
    console.log(`[GlobalSearch] Search completed in ${(endTime - startTime).toFixed(2)}ms`)

    return results
  }, [query])

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0)
  }, [searchResults])

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus()
      setQuery('')
      setSelectedIndex(0)
    }
  }, [isOpen])

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev + 1) % searchResults.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev - 1 + searchResults.length) % searchResults.length)
      } else if (e.key === 'Enter' && searchResults[selectedIndex]) {
        e.preventDefault()
        navigate(searchResults[selectedIndex].path)
        onClose()
      }
    },
    [searchResults, selectedIndex, navigate, onClose]
  )

  // Navigate to result
  const handleResultClick = (path: string) => {
    navigate(path)
    onClose()
  }

  // Highlight matching text
  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text

    const parts = text.split(new RegExp(`(${query})`, 'gi'))
    return (
      <span>
        {parts.map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={i} className="global-search__highlight">
              {part}
            </mark>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </span>
    )
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div className="global-search__backdrop" onClick={onClose} role="presentation" />

      {/* Modal */}
      <div
        className="global-search"
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-title"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="global-search__header">
          <Search className="global-search__icon" size={20} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            className="global-search__input"
            placeholder="Search pages, projects, and more..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search"
            id="search-title"
          />
          <button
            className="global-search__close"
            onClick={onClose}
            aria-label="Close search"
            type="button"
          >
            <X size={20} />
          </button>
        </div>

        {/* Results */}
        <div className="global-search__results" ref={resultsRef}>
          {query.trim() === '' ? (
            <div className="global-search__empty">
              <p>Type to search across all content</p>
              <div className="global-search__shortcuts">
                <kbd>↑</kbd> <kbd>↓</kbd> to navigate
                <span className="global-search__separator">•</span>
                <kbd>Enter</kbd> to select
                <span className="global-search__separator">•</span>
                <kbd>Esc</kbd> to close
              </div>
            </div>
          ) : searchResults.length === 0 ? (
            <div className="global-search__empty">
              <p>No results found for "{query}"</p>
            </div>
          ) : (
            searchResults.map((result, index) => (
              <button
                key={`${result.type}-${result.path}`}
                className={`global-search__result ${
                  index === selectedIndex ? 'global-search__result--selected' : ''
                }`}
                onClick={() => handleResultClick(result.path)}
                onMouseEnter={() => setSelectedIndex(index)}
                type="button"
              >
                <div className="global-search__result-type">{result.type}</div>
                <div className="global-search__result-content">
                  <div className="global-search__result-title">
                    {highlightMatch(result.title, query)}
                  </div>
                  {result.description && (
                    <div className="global-search__result-description">
                      {highlightMatch(result.description, query)}
                    </div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        {searchResults.length > 0 && (
          <div className="global-search__footer">
            {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </>
  )
}

GlobalSearch.displayName = 'GlobalSearch'

/**
 * Hook to manage global search keyboard shortcut
 */
export function useGlobalSearch() {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsOpen(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return {
    isOpen,
    openSearch: () => setIsOpen(true),
    closeSearch: () => setIsOpen(false),
  }
}
